// Formulaire de dépôt et de modification (porté de FeedbackComposer) :
// description d'abord (le titre se déduit), doublons proposés pendant la
// saisie, pièces jointes, contexte capturé, contexte métier de l'app.
import type { JSX } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import type { FeedbackItem, FeedbackType, Media, SimilarItem } from '@feedbaacks/sdk'
import { capturePage, consoleSnapshot, pageSnapshot } from '../capture'
import { useFeedbaacks } from '../context'
import { Check, Send, X } from '../icons'
import { Button, IconButton, Spinner } from '../primitives'
import { cx, errorMessage } from '../util'
import { Detail } from './Detail'
import { MediaUploader, ScreenshotAttacher } from './media'
import { ContextEdit, SimilarSuggestions, useAppTypes } from './parts'
import { TYPE_META } from './pills'

const DEBOUNCE_MS = 300

export function Composer({ onDone }: { onDone?: (item: FeedbackItem) => void }): JSX.Element {
  const { client, store, t, options, role, notify, confirm, changes } = useFeedbaacks()
  const team = role === 'admin' || role === 'staff'
  const capture = options.capture ?? {}
  const types = useAppTypes()
  const [editId] = useState(() => store.get().editingId)
  const [initialDraft] = useState(() => (editId ? null : store.get().draft))
  const [type, setType] = useState<FeedbackType>(() => store.get().pendingType ?? initialDraft?.type ?? 'feature')
  const [title, setTitle] = useState(initialDraft?.title ?? '')
  const [body, setBody] = useState(initialDraft?.body ?? '')
  const [titleShown, setTitleShown] = useState(!!initialDraft?.title)
  const [medias, setMedias] = useState<Media[]>([])
  const [snapshot] = useState(pageSnapshot)
  const [pageUrl, setPageUrl] = useState(snapshot.page_url)
  const [screenshot, setScreenshot] = useState<string | null>(null)
  const [includeConsole, setIncludeConsole] = useState(true)
  const [extraContext, setExtraContext] = useState<Record<string, unknown>>({})
  const [similar, setSimilar] = useState<SimilarItem[]>([])
  const [preview, setPreview] = useState<FeedbackItem | null>(null)
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [shaking, setShaking] = useState(false)
  const bodyRef = useRef<HTMLTextAreaElement>(null)
  const titleRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    store.set({ pendingType: null })
    bodyRef.current?.focus()
  }, [store])

  // Modification : le formulaire se remplit une fois, depuis le retour.
  useEffect(() => {
    if (!editId) return
    client.feedback.get(editId).then((it) => {
      setType(it.type)
      setTitle(it.title)
      setTitleShown(true)
      setBody(it.body)
      setMedias(it.medias)
    }, (err: unknown) => notify('error', errorMessage(err, t('load_error'))))
  }, [client, editId])

  // Le brouillon survit au détour par une suggestion et à la fermeture.
  useEffect(() => {
    if (!editId) store.set({ draft: title || body ? { type, title, body } : null })
  }, [editId, type, title, body, store])

  // Doublons : la description alimente la recherche quand il n'y a pas de titre.
  const seed = title.trim() || body.trim()
  useEffect(() => {
    if (editId || seed.length < 3) {
      setSimilar([])
      return
    }
    const timer = setTimeout(() => client.feedback.searchSimilar(seed).then(setSimilar, () => setSimilar([])), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [seed, editId, client])

  const valid = title.trim().length >= 3 || body.trim().length >= 3
  const close = () => store.set({ composerOpen: false, editingId: null, draft: null })

  const submit = async () => {
    if (!valid || sending || sent) return
    setSending(true)
    try {
      let item: FeedbackItem
      if (editId) {
        item = await client.feedback.update(editId, { title: title.trim() || undefined, body: body.trim(), type: team ? type : undefined, medias })
        options.onEvent?.({ type: 'updated', item })
      } else {
        const isBug = type === 'bug'
        let shot = screenshot
        // Bug d'un utilisateur : capture jointe en silence, pour l'équipe seule.
        if (!team && isBug && !shot && (capture.screenshot ?? 'bug') === 'bug') {
          shot = await (capture.takeScreenshot ?? capturePage)().then((b) => client.feedback.uploadScreenshot(b)).catch(() => null)
        }
        const context = { ...(options.context ? await options.context() : {}), ...extraContext }
        const page = capture.page === false ? {} : { page_url: pageUrl || undefined, viewport: snapshot.viewport || undefined, user_agent: snapshot.user_agent || undefined }
        item = await client.feedback.create({
          type,
          title: title.trim() || undefined,
          body: body.trim() || undefined,
          ...page,
          screenshot_url: shot ?? undefined,
          console_errors: isBug && capture.console !== false && (!team || includeConsole) ? consoleSnapshot() : undefined,
          medias: medias.length ? medias : undefined,
          context: Object.keys(context).length ? context : undefined,
        })
        options.onEvent?.({ type: 'created', item })
      }
      setSent(true)
      changes.emit({ item })
      setTimeout(() => {
        store.set({ composerOpen: false, editingId: null, draft: null, selectedId: item.id })
        onDone?.(item)
      }, 600)
    } catch (err) {
      setShaking(true)
      setTimeout(() => setShaking(false), 220)
      notify('error', errorMessage(err, t(editId ? 'composer_update_failed' : 'composer_submit_failed')))
    } finally {
      setSending(false)
    }
  }

  const cancel = async () => {
    if ((title.trim() || body.trim()) && !(await confirm({ title: t('composer_cancel_title'), message: t('composer_cancel_confirm'), confirmLabel: t('composer_cancel_yes'), cancelLabel: t('composer_continue') }))) return
    close()
  }

  const placeholder = type === 'bug' ? t('composer_title_placeholder_bug') : type === 'question' ? t('composer_title_placeholder_question') : t('composer_body_placeholder_feature')

  return (
    <form class={cx('fb-composer', shaking && 'fb-shake')} noValidate data-testid="feedbaacks-composer"
      onSubmit={(e) => {
        e.preventDefault()
        void submit()
      }}
      onKeyDown={(e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
          e.preventDefault()
          void submit()
        } else if (e.key === 'Escape' && !preview) {
          e.stopPropagation()
          void cancel()
        }
      }}>
      {preview && (
        <div class="fb-preview" role="dialog" aria-modal="true" aria-label={t('preview_title')} data-testid="feedbaacks-suggestion-preview">
          <div class="fb-preview-bar">
            <span class="fb-muted">{t('preview_title')} · <span class="fb-strong">{preview.title}</span></span>
            <IconButton label={t('preview_close')} onClick={() => setPreview(null)}><X size={16} /></IconButton>
          </div>
          <div class="fb-scroll"><Detail item={preview} onChange={setPreview} /></div>
        </div>
      )}
      <div class="fb-scroll fb-composer-body">
        <fieldset class="fb-type-choices">
          <legend class="fb-sr-only">{t('type_legend')}</legend>
          {types.map((value) => {
            const M = TYPE_META[value]
            return (
              <button key={value} type="button" aria-pressed={type === value} class={cx('fb-type-choice', `fb-type-${value}`, type === value && 'is-active')}
                onClick={() => setType(value)} disabled={!!editId && !team} data-testid={`feedbaacks-type-${value}`}>
                <M.Icon size={18} />
                <span>{t(M.key)}</span>
              </button>
            )
          })}
        </fieldset>
        {titleShown && (
          <input ref={titleRef} class="fb-title-input" type="text" value={title} maxLength={200} placeholder={t('composer_title_placeholder')}
            aria-label={t('composer_title_aria')} data-testid="feedbaacks-title-input" onInput={(e) => setTitle(e.currentTarget.value)} />
        )}
        <div class="fb-relative">
          <textarea ref={bodyRef} class="fb-textarea is-main" rows={5} value={body} placeholder={placeholder} aria-label={t('composer_body_aria')}
            data-testid="feedbaacks-body-input" onInput={(e) => setBody(e.currentTarget.value)} />
          <SimilarSuggestions items={similar} onPreview={(id) => client.feedback.get(id).then(setPreview, () => {})} />
        </div>
        {!titleShown && (
          <button type="button" class="fb-link fb-small" data-testid="feedbaacks-add-title"
            onClick={() => {
              setTitleShown(true)
              setTimeout(() => titleRef.current?.focus(), 0)
            }}>
            {t('composer_add_title')}
          </button>
        )}
        {options.slots?.composer?.({ setContext: (patch) => setExtraContext((c) => ({ ...c, ...patch })) })}
        <MediaUploader medias={medias} onChange={setMedias} />
        {!editId && team && (
          <ContextEdit pageUrl={pageUrl} onPageUrl={setPageUrl} viewport={snapshot.viewport} userAgent={snapshot.user_agent}
            screenshot={capture.screenshot === 'off' ? undefined : <ScreenshotAttacher url={screenshot} onChange={setScreenshot} />}
            consoleCount={type === 'bug' && capture.console !== false ? consoleSnapshot().length : undefined}
            consoleEnabled={includeConsole} onConsoleEnabled={setIncludeConsole} />
        )}
      </div>
      <footer class="fb-composer-footer">
        <Button variant="ghost" onClick={() => void cancel()}>{t('composer_cancel')}</Button>
        <Button type="submit" variant="primary" disabled={!valid || sending || sent} testId="feedbaacks-submit">
          {sent ? <Check size={16} /> : sending ? <Spinner size={16} /> : <Send size={16} />}
          {sent ? t(editId ? 'composer_saved' : 'composer_submit_sent') : sending ? t(editId ? 'composer_saving' : 'composer_submit_sending') : t(editId ? 'composer_save' : 'composer_submit')}
          {!sent && !sending && <kbd class="fb-kbd">⌘↵</kbd>}
        </Button>
      </footer>
    </form>
  )
}
